import React, { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useMutation, useQuery } from '@apollo/client';

import {
  ADD_MODEL_TAG_QUERY,
  DELETE_MODEL_TAG_QUERY,
  FETCH_MODEL_ASSOC_PIPELINES,
  FETCH_MODEL_QUERY,
  FETCH_MODEL_VERSIONS_QUERY,
  transformModelResponse,
} from '../../utils/model_server';

import { modelDetailTimeRangePollInterval } from '../../config';

import useWorkspaceContext from '../workspaces/WorkspaceContext/WorkspaceContext';

const ModelDataContext = React.createContext({
  error: null,
  historyError: null,
  loading: false,
  historyLoading: false,
  model: null,
  relatedModels: [],
  addModelTag: () => {},
  deleteModelTag: () => {},
});

const useModelTagsQuery = ({ modelId, modelVersion, model }) => {
  const [addModelTag] = useMutation(ADD_MODEL_TAG_QUERY, {
    update: (cache, { data: { insert_model_tag_one } }) => {
      const existingModels = cache.readQuery({
        query: FETCH_MODEL_QUERY,
        variables: { model_id: modelId, model_version: modelVersion },
      });

      const newModels = existingModels.model.map(existingModel => insert_model_tag_one.model_id === existingModel.id
        ? { ...existingModel, model_tags: existingModel.model_tags.concat([insert_model_tag_one]) }
        : existingModel);

      cache.writeQuery({
        query: FETCH_MODEL_QUERY,
        variables: { model_id: modelId, model_version: modelVersion },
        data: { model: newModels },
      });
    },
  });
  const handleAddTag = (newTag) => {
    if (!model || model.modelTags.some(({ tag }) => tag.tag === newTag)) {
      return;
    }

    addModelTag({
      variables: { model_id: model.id, tag_name: newTag },
      optimisticResponse: {
        insert_model_tag_one: {
          id: `temp-model-tag-id__${newTag}`,
          model_id: model.id,
          __typename: 'model_tag',
          tag_id: `temp-tag-id__${newTag}`,
          tag: {
            id: `temp-tag-id__${newTag}`,
            tag: newTag,
            __typename: 'tag',
          },
        },
      },
    });
  };

  const [deleteModelTag] = useMutation(DELETE_MODEL_TAG_QUERY, {
    update: (cache, { data: { delete_model_tag } }) => {
      const deletedModelTagIDs = delete_model_tag.returning.map(({ id }) => id);

      const existingModels = cache.readQuery({
        query: FETCH_MODEL_QUERY,
        variables: { model_id: modelId, model_version: modelVersion },
      });

      const newModels = existingModels.model.map(existingModel => ({
        ...existingModel,
        model_tags: existingModel.model_tags.filter(({ id }) => !deletedModelTagIDs.includes(id)),
      }));

      cache.writeQuery({
        query: FETCH_MODEL_QUERY,
        variables: { model_id: modelId, model_version: modelVersion },
        data: { model: newModels },
      });
    },
  });
  const handleDeleteTag = (deletedTag) => {
    if (!model) {
      return;
    }

    const modelTag = model.modelTags.find(({ tag }) => tag.tag === deletedTag);
    if (!modelTag) {
      return;
    }

    deleteModelTag({
      variables: { model_tag_id: modelTag.id },
      optimisticResponse: {
        delete_model_tag: {
          returning: [{ __typename: 'model_tag', id: modelTag.id }],
          __typename: 'model_tag_mutation_response',
        },
      },
    });
  };

  return { addModelTag: handleAddTag, deleteModelTag: handleDeleteTag };
}

const useModelQuery = ({ modelId, modelVersion }) => {
  const { loading, error, data } = useQuery(FETCH_MODEL_QUERY, {
    variables: { model_id: modelId, model_version: modelVersion },
    pollInterval: modelDetailTimeRangePollInterval,
  });
  const model = !loading && !error && data.model.length === 1 ? transformModelResponse(data.model[0]) : null;

  return {
    model, loading, error
  };
}

const useModelHistory = ( modelId, currentWorkspaceId ) => {
  const modelVersionsQueryResult = useQuery(FETCH_MODEL_VERSIONS_QUERY, {
    variables: { model_id: modelId, workspace_id: currentWorkspaceId },
    fetchPolicy: 'cache-and-network',
    pollInterval: modelDetailTimeRangePollInterval,
  });

  const models
    = !!modelVersionsQueryResult.data?.model && !modelVersionsQueryResult.error
      ? modelVersionsQueryResult.data.model.map(transformModelResponse)
      : [];

  const relatedModels = models.filter(({ modelID }) => modelID === modelId);

  return {
    models: relatedModels,
    loading: modelVersionsQueryResult?.loading,
    error: modelVersionsQueryResult?.error,
  }
}

// Returns an array
const useModelAssociations = (modelId) => {
  const modelAssocPipelinesQueryRes = useQuery(FETCH_MODEL_ASSOC_PIPELINES, {
    variables: { modelId },
    pollInterval: 1000,
    // pollInterval: modelDetailTimeRangePollInterval,
  });
  
  return modelAssocPipelinesQueryRes.data?.deployment_model_configs.map((modelAndPipeline) => {
    const { model_config: { model: { model_id, model_version } }, pipeline_version: { pipeline: { pipeline_id } } } = modelAndPipeline;
    return {
      model_id,
      model_version,
      pipeline_id
    }
  }) || [];
}

export const ModelDataProvider = ({ children, enableAnomalyDetection = false }) => {
  const routeParams = useParams();
  const { currentWorkspaceId } = useWorkspaceContext();

  const { loading, error, model } = useModelQuery({
    modelId: routeParams?.id,
    modelVersion: routeParams?.version,
  })
  const { loading: historyLoading, error: historyError, models: relatedModels } = useModelHistory(routeParams?.id, currentWorkspaceId);
  const { addModelTag, deleteModelTag } = useModelTagsQuery({ modelId: routeParams?.id, modelVersion: routeParams?.version, model });
  const modelAssociations = useModelAssociations(model?.id);

  const modelDataContextValue = useMemo(() => ({
    model,
    loading,
    error,
    relatedModels,
    historyError,
    historyLoading,
    isAnomalyDetectionEnabled: enableAnomalyDetection,
    addModelTag,
    deleteModelTag,
    modelAssociations,
  }), [
    model,
    loading,
    error,
    relatedModels,
    historyError,
    historyLoading,
    enableAnomalyDetection,
    addModelTag,
    deleteModelTag,
    modelAssociations,
  ])

  return (
    <ModelDataContext.Provider
      value={modelDataContextValue}
    >
      { children }
    </ModelDataContext.Provider>
  )
};

export const useModelData = () => useContext(ModelDataContext)

export default useModelData;
